<template>
  <div>
    <v-card width="400">
      <v-card-title>
        <DialogTitle :dialogName="$options.name">
          {{ isEditMode ? 'Edit' : 'New' }} Sync Interface
        </DialogTitle>
      </v-card-title>
      <v-card-text v-if="formData && selectLists">
        <v-text-field
          v-model="formData.Description"
          label="Description"
          :error-messages="errorMessages(vuelidate.formData.Description)"
          @blur="vuelidate.formData.Description.$touch()"
          dense
        />
        <v-select
          :disabled="isEditMode"
          v-model="formData.InterfaceType"
          label="Interface Type"
          :items="selectLists.InterfaceTypes"
          item-text="Text"
          item-value="Value"
          :error-messages="errorMessages(vuelidate.formData.InterfaceType)"
          @blur="vuelidate.formData.InterfaceType.$touch()"
          dense
        />
        <UserPicker
          v-model="formData.UserId"
          label="User for all sync operations"
          :error-messages="errorMessages(vuelidate.formData.UserId)"
          @blur="vuelidate.formData.UserId.$touch()"
        />
      </v-card-text>
      <v-card-actions
        ><v-btn color="success" class="ml-1" @click="saveClick">{{
          isEditMode ? 'Update' : 'Save'
        }}</v-btn>
        <v-btn color="error" class="ml-3" @click="cancelClick">Cancel</v-btn>
      </v-card-actions>
    </v-card>

    <DialogUtil ref="dialogUtil" />
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { host } from '@/services/HostAPI'
import { selectListCache } from '@/services/SelectListCache'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
export default {
  name: 'SyncWithEditor',
  props: {
    infoId: {
      type: String,
      default: null
    },
    isDialogOpen: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const dialogUtil = ref()

    const isEditMode = ref(false)
    const selectLists = ref()
    const formData = ref()

    watch(
      () => props.isDialogOpen,
      newVal => {
        newVal && initDialog()
      }
    )

    const initDialog = async () => {
      isEditMode.value = props.infoId != null
      await loadSelectLists()
      isEditMode.value = props.infoId != null
      if (isEditMode.value) {
        await loadSyncWith()
      } else {
        await initNewSyncWith()
      }
      vuelidate.value.$reset()
    }

    const loadSelectLists = async () => {
      const selectListNames = [
        selectListCache.name.Users,
        selectListCache.name.InterfaceTypes
      ]

      selectLists.value = await selectListCache.get(selectListNames)
    }

    const loadSyncWith = async () => {
      const rq = { InfoId: props.infoId }
      const rs = await host.syncWith.retrieve(rq)
      formData.value = rs.Info
    }

    const initNewSyncWith = async () => {
      const rq = {}
      const rs = await host.syncWith.newTemplate(rq)
      formData.value = rs.Info
    }

    const saveClick = async () => {
      if (validate()) {
        return
      }

      const result = isEditMode.value ? await update() : await saveNew()
      afterSaveOrUpdate(result)
    }

    const cancelClick = () => {
      context.emit('close')
    }

    const afterSaveOrUpdate = result => {
      if (result.IsSuccess) {
        context.emit('update')
      } else {
        dialogUtil.value.error(result.Message)
      }
    }

    const rules = {
      formData: {
        Description: { required },
        InterfaceType: { required },
        UserId: { required }
      }
    }

    const vuelidate = useVuelidate(rules, { formData })

    const errorMessages = item => {
      return vuelidate.value.$invalid ? item.$errors.map(e => e.$message) : []
    }

    const validate = () => {
      vuelidate.value.$touch()
      return vuelidate.value.$invalid
    }

    const saveNew = async () => {
      const rq = { Info: formData.value }
      const rs = await host.syncWith.create(rq)
      return rs
    }

    const update = async () => {
      const rq = { Info: formData.value }
      const rs = await host.syncWith.update(rq)
      return rs
    }

    initDialog()
    return {
      dialogUtil,
      isEditMode,
      selectLists,
      formData,
      errorMessages,
      saveClick,
      cancelClick,
      vuelidate
    }
  }
}
</script>

<style lang="scss" scoped></style>
